import * as React from 'react'

import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react'
import { DayButton, DayPicker } from 'react-day-picker'

import { Button } from '@/components/shadcn-ui/button'
import { cn } from '@/lib/utils'

// react-day-picker v9 renders the month as a real <table> (thead/tbody/tr/th/td),
// and `react-day-picker/style.css` is imported globally (unlayered) for the
// legacy ICS picker. Every UI slot is therefore styled explicitly here — with
// table-compatible classes, not flexbox — so the default `rdp-*` rules don't win.
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'dropdown',
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      className={cn('tw:bg-background tw:p-3', className)}
      formatters={{
        formatMonthDropdown: date =>
          date.toLocaleString('default', { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: 'tw:w-fit',
        months: 'tw:relative tw:flex tw:flex-col',
        month: 'tw:relative tw:flex tw:flex-col tw:gap-3',
        // Nav spans the full width over the caption; make the container
        // click-through so the centered dropdowns stay clickable, and only the
        // arrow buttons capture clicks.
        nav: 'tw:pointer-events-none tw:absolute tw:inset-x-0 tw:top-0 tw:z-10 tw:flex tw:h-7 tw:items-center tw:justify-between',
        button_previous:
          'tw:pointer-events-auto tw:inline-flex tw:size-7 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-md tw:border-0 tw:bg-transparent tw:text-muted-foreground tw:select-none tw:hover:bg-muted tw:aria-disabled:opacity-50',
        button_next:
          'tw:pointer-events-auto tw:inline-flex tw:size-7 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-md tw:border-0 tw:bg-transparent tw:text-muted-foreground tw:select-none tw:hover:bg-muted tw:aria-disabled:opacity-50',
        month_caption:
          'tw:flex tw:h-7 tw:items-center tw:justify-center tw:px-7',
        dropdowns:
          'tw:flex tw:items-center tw:justify-center tw:gap-2 tw:text-sm tw:font-medium',
        dropdown_root:
          'tw:relative tw:inline-flex tw:cursor-pointer tw:items-center tw:gap-1 tw:rounded-md tw:px-1 tw:hover:bg-muted',
        dropdown: 'tw:absolute tw:inset-0 tw:cursor-pointer tw:opacity-0',
        caption_label:
          'tw:flex tw:items-center tw:gap-1 tw:text-sm tw:font-medium tw:select-none tw:[&>svg]:size-3.5 tw:[&>svg]:text-muted-foreground',
        month_grid: 'tw:w-full tw:border-collapse',
        weekdays: '',
        weekday:
          'tw:pb-1 tw:text-center tw:text-[0.8rem] tw:font-normal tw:text-muted-foreground tw:select-none',
        week: '',
        day: 'tw:p-0.5 tw:text-center',
        day_button: '',
        today: '',
        selected: '',
        range_start: '',
        range_middle: '',
        range_end: '',
        outside: '',
        disabled: '',
        hidden: 'tw:invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation, className: iconClassName, ...iconProps }) => {
          if (orientation === 'left') {
            return (
              <ChevronLeftIcon
                className={cn('tw:size-4', iconClassName)}
                {...iconProps}
              />
            )
          }
          if (orientation === 'right') {
            return (
              <ChevronRightIcon
                className={cn('tw:size-4', iconClassName)}
                {...iconProps}
              />
            )
          }
          return (
            <ChevronDownIcon
              className={cn('tw:size-4', iconClassName)}
              {...iconProps}
            />
          )
        },
        DayButton: CalendarDayButton,
        ...components,
      }}
      {...props}
    />
  )
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <Button
      ref={ref}
      variant="ghost"
      data-day={day.date.toLocaleDateString()}
      data-selected={modifiers.selected || undefined}
      data-today={(modifiers.today && !modifiers.selected) || undefined}
      data-outside={modifiers.outside || undefined}
      className={cn(
        'tw:mx-auto tw:flex tw:size-9 tw:items-center tw:justify-center tw:rounded-md tw:border-0 tw:p-0 tw:text-sm tw:font-normal',
        'tw:data-[outside=true]:text-muted-foreground',
        'tw:data-[today=true]:bg-muted tw:data-[today=true]:font-medium',
        'tw:data-[selected=true]:bg-primary tw:data-[selected=true]:font-medium tw:data-[selected=true]:text-primary-foreground tw:data-[selected=true]:hover:bg-primary',
        className,
      )}
      {...props}
    />
  )
}

export { Calendar, CalendarDayButton }
